import { ProfileItem } from '@/components/profile-item'
import { Can } from '@/components/providers/ability-provider'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { useTRPC } from '@/trpc/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Building, Building2, CalendarDays, Clock, CreditCard, MapPin } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/business_info/')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const {
    data: business,
    isPending: isPendingBusiness,
    refetch: refetchBusiness,
  } = useQuery({
    ...api.business.getMyBusiness.queryOptions(),
  })

  const { data: orgLogoUrl, refetch: refetchOrganizationLogo } = useQuery({
    ...api.business.getOrganizationLogo.queryOptions(),
  })

  const uploadMutation = useMutation({
    ...api.business.uploadOrganizationLogo.mutationOptions(),
  })

  const updateBusinessNameMutation = useMutation({
    ...api.business.updateBusinessName.mutationOptions({
      onSuccess: async () => {
        toast.success('Business name updated successfully')
        await refetchBusiness()
      },
      onError: (error) => {
        console.error('Error updating business name', error)
        toast.error('Failed to update business name')
      },
    }),
  })

  const [newBusinessName, setNewBusinessName] = useState('')

  useEffect(() => {
    if (business?.name) {
      setNewBusinessName(business.name)
    }
  }, [business?.name])

  async function readAndProcessImage(file: File): Promise<string> {
    if (!file.type.startsWith('image/')) {
      throw new Error('Invalid file type')
    }
    const tenMb = 10 * 1024 * 1024
    if (file.size > tenMb) {
      throw new Error('File exceeds 10MB')
    }

    const img = await createImageBitmap(file)
    const maxSize = 1024
    const scale = Math.min(1, maxSize / Math.max(img.width, img.height))
    const width = Math.max(1, Math.round(img.width * scale))
    const height = Math.max(1, Math.round(img.height * scale))
    if (img.width > 1024 || img.height > 1024) {
      // downscale
    }
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas not supported')
    ctx.drawImage(img, 0, 0, width, height)
    const dataUrl = canvas.toDataURL('image/webp', 0.9)
    // validate size again
    const base64Data = dataUrl.split(',')[1] ?? ''
    const bufferLength = Math.floor((base64Data.length * 3) / 4)
    if (bufferLength > tenMb) {
      throw new Error('Processed image exceeds 10MB')
    }
    return dataUrl
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]!
    try {
      const dataUrl = await readAndProcessImage(file)
      const p = uploadMutation.mutateAsync({ imageBase64: dataUrl })
      toast.promise(p, {
        loading: 'Uploading logo…',
        success: 'Logo uploaded',
        error: (e) => e?.message ?? 'Upload failed',
      })
      await p
      await refetchOrganizationLogo()
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Invalid image'
      toast.error(message)
    }
  }

  // if (isPending || !business) return <Skeleton className="h-10 w-48" />

  const businessPlan = business?.plan ?? 'free'

  return (
    <Can do="read" on="Business">
      <div className="container py-10">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4">
                {orgLogoUrl ? (
                  <img
                    src={orgLogoUrl}
                    alt="Organization logo"
                    // className="h-16 w-16 rounded-md object-cover border"
                    className="h-16 w-16 rounded-md object-contain border bg-muted"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-md border bg-muted" />
                )}
                <div>
                  {isPendingBusiness || !business ? (
                    <>
                      <Skeleton className="h-10 w-48 bg-neutral-300" />
                      <p className="text-sm text-muted-foreground">Business Profile</p>
                    </>
                  ) : (
                    <>
                      <CardTitle className="text-2xl">{business.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">Business Profile</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <Separator className="mb-4" />
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div
                    onDragOver={(e) => {
                      e.preventDefault()
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      handleFiles(e.dataTransfer.files)
                    }}
                    className="flex flex-col items-center justify-center rounded-md border border-dashed p-6 text-center"
                  >
                    <p className="text-sm text-muted-foreground mb-2">
                      Drag &amp; drop an image here, or choose a file
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFiles(e.currentTarget.files)}
                      />
                      <Button
                        type="button"
                        onClick={() => {
                          const input = document.createElement('input')
                          input.type = 'file'
                          input.accept = 'image/*'
                          input.onchange = (e: Event) => {
                            const target = e.target as HTMLInputElement
                            handleFiles(target.files)
                          }
                          input.click()
                        }}
                        disabled={uploadMutation.isPending}
                      >
                        {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Max 1024×1024px, 10MB. We will downscale larger images.
                    </p>
                  </div>
                </div>
                {business && (
                  <div className="space-y-4">
                    <ProfileItem
                      icon={Building2}
                      label="Business ID"
                      value={business.businessId}
                    />
                    <ProfileItem icon={MapPin} label="Location" value={business.location ?? ''} />
                    <ProfileItem
                      icon={CalendarDays}
                      label="Created At"
                      value={business.createdAt.toLocaleString()}
                    />
                    <ProfileItem
                      icon={Clock}
                      label="Last Updated"
                      value={business.updatedAt.toLocaleString()}
                    />
                    <ProfileItem
                      icon={CreditCard}
                      label="Plan"
                      value={businessPlan?.charAt(0)?.toUpperCase() + businessPlan?.slice(1)}
                    />
                    <ProfileItem
                      icon={Building}
                      label="Business Type"
                      value={
                        business.type === 'staffing_agency' ? 'Staffing Agency' : 'Direct Employer'
                      }
                    />
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <Can do="update" on="Business">
                  <div className="space-y-2">
                    <h2 className="text-sm font-medium text-muted-foreground">Business Name</h2>
                    <p className="text-xs text-muted-foreground">
                      Update your organization&apos;s display name.
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Input
                        value={newBusinessName}
                        onChange={(e) => setNewBusinessName(e.target.value)}
                        placeholder="Enter business name"
                        disabled={updateBusinessNameMutation.isPending || !business}
                      />
                      <Button
                        type="button"
                        onClick={() => {
                          const trimmedName = newBusinessName.trim()
                          if (!trimmedName) {
                            toast.error('Business name is required')
                            return
                          }
                          updateBusinessNameMutation.mutate({ name: trimmedName })
                        }}
                        disabled={
                          updateBusinessNameMutation.isPending ||
                          !business ||
                          newBusinessName.trim() === business?.name
                        }
                      >
                        {updateBusinessNameMutation.isPending ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  </div>
                </Can>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Can>
  )
}
